import { useEffect, useRef } from 'react'
import { Alert, Box, Button, Divider, IconButton, Snackbar, Stack, Tooltip } from '@mui/material'
import { Moon, Sun } from 'lucide-react'
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom'
import { InternalBand, Wordmark } from '@/components'
import { useAuth } from '@/auth'
import { glassSurface, layout } from '@/theme/tokens'
import type { Mode } from '@/theme/createInternalTheme'
import { AccountMenu } from './AccountMenu'
import { ErrorBoundary } from './ErrorBoundary'
import { SECTIONS, activeSection } from './nav'

/**
 * The chrome: the band, the section bar, and whatever the route renders.
 *
 * ── FIVE SECTIONS, NOT SEVENTEEN LINKS ──────────────────────────────────────
 * This was a flex-wrapping row of seventeen `NavLink`s in four unnamed groups,
 * and its own comment recorded that the row had stopped fitting and was pushing
 * the provenance legend off the right edge. The groups existed only as a
 * comment; now they are the navigation, and each one's screens are tabs inside
 * it.
 *
 * The sections are a plain `<nav>` of links rather than a second tablist: one
 * tablist per page is correct ARIA, and two nested ones are both wrong and
 * visually heavy.
 *
 * ── AND `<main>` OWNS THE SCROLL ────────────────────────────────────────────
 * The document used to scroll, which meant `InternalBand` — the marker whose
 * entire job is to survive a screenshot crop — scrolled off the top, so a
 * screenshot taken below the fold lost it. With the scroll inside `<main>`, the
 * band and the section bar are structurally permanent.
 */
export function AppShell({ mode, onToggleMode }: { mode: Mode; onToggleMode: () => void }) {
  const { pathname } = useLocation()
  const section = activeSection(pathname)
  const main = useRef<HTMLElement>(null)

  // `<BrowserRouter>` has no `<ScrollRestoration/>` — that is data-router only —
  // so without this every navigation lands halfway down the previous screen.
  useEffect(() => {
    main.current?.scrollTo({ top: 0 })
  }, [pathname])

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
      <InternalBand />

      <Box
        component="header"
        sx={(t) => ({
          ...glassSurface(t),
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          minHeight: layout.header.height,
          flexShrink: 0,
          borderWidth: 0,
          borderBottomWidth: 1,
          borderStyle: 'solid',
          borderColor: 'divider',
        })}
      >
        <Box component={RouterLink} to="/" sx={{ color: 'inherit', textDecoration: 'none', mr: 1 }}>
          <Wordmark />
        </Box>

        {/* ── DESPLAZA, NO ENVUELVE ────────────────────────────────────────
            `flexWrap: 'wrap'` dejaba que la fila de secciones creciera a dos o
            tres renglones en una ventana angosta, y el encabezado con ella: el
            `minHeight` es un piso, no un techo. En una ventana baja —una laptop
            de 13" con el navegador a media pantalla— eso se come el área que de
            verdad se lee, y `<main>` es lo único que puede encoger. Cinco
            secciones caben casi siempre; cuando no, se desplazan de lado, que
            es la dimensión donde sobra espacio. */}
        <Stack
          component="nav"
          aria-label="Secciones"
          direction="row"
          spacing={0.5}
          sx={{
            flex: 1,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            // La barra de scroll horizontal en un contenedor de 40px de alto
            // ocuparía una porción absurda del control que intenta mostrar.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            // Sin esto los botones se comprimen hasta partir la palabra en vez
            // de desbordar, y «Plataforma» sale en tres renglones de una letra.
            '& > *': { flexShrink: 0 },
          }}
        >
          {SECTIONS.map((s) => {
            const current = section?.id === s.id
            return (
              <Button
                key={s.id}
                component={RouterLink}
                to={s.to}
                size="small"
                aria-current={current ? 'page' : undefined}
                sx={{
                  color: current ? 'primary.main' : 'text.secondary',
                  fontWeight: current ? 700 : 500,
                  bgcolor: current ? 'action.selected' : undefined,
                }}
              >
                {s.label}
              </Button>
            )
          })}
        </Stack>

        <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
        <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
          <IconButton onClick={onToggleMode} aria-label="cambiar tema">
            {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </IconButton>
        </Tooltip>
        <AccountMenu />
      </Box>

      <Box
        component="main"
        ref={main}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: layout.page.paddingX,
          py: layout.page.paddingY,
        }}
      >
        {/* El cinturón, sobre los tirantes de `SectionLayout`: `NotFoundPage` y
            cualquier ruta futura cuelgan directo de aquí, fuera de toda sección,
            y sin esto una excepción suya seguiría vaciando la raíz entera. */}
        <ErrorBoundary scope="la consola">
          <Outlet />
        </ErrorBoundary>
      </Box>

      <SessionBanner />
    </Box>
  )
}

/**
 * The session ended while the page was open.
 *
 * ── THE DATA STAYS ON SCREEN, DELIBERATELY ──────────────────────────────────
 * Every panel carries its own `generated_at` in its provenance footer, so what
 * is already rendered remains true AS OF THAT TIMESTAMP. Blanking it would
 * destroy something the reader may be mid-screenshot on, to no benefit — the
 * numbers did not become wrong, the session did. A banner names the cause once,
 * where an expiry otherwise arrives as a screen full of red panels that reads
 * like an outage and gets escalated as one.
 *
 * This is the one place in this app where a modal would be the wrong
 * instrument: it would cover the thing the user is looking at in order to tell
 * them about something that did not change it.
 */
function SessionBanner() {
  const { expired, login } = useAuth()
  return (
    <Snackbar open={expired} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button color="inherit" size="small" onClick={login}>
            Volver a entrar
          </Button>
        }
      >
        La sesión expiró. Lo que está en pantalla sigue siendo válido con la fecha de su pie.
      </Alert>
    </Snackbar>
  )
}
